import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, Select, Alert, Spinner, Dialog, DialogTitle, DialogContent, DialogActions } from '@glplus/glpweb-dsl';
import { vendorApiClient, type AssetKind, type ChangeField, type ChangeRequest, type ProductDetail } from '../../lib/vendorApiClient';
import { BORDER, HEADING, MUTED, FieldLabel, departmentPath, describeProductError } from './parts';

/**
 * P3 — Request a change (Figma `fVTA2OnSEz4UOdTeoXeByH` 57:158).
 *
 * Product information is shared with shoppers and recipes, so a change to it
 * is asked for, reviewed, and what is there now keeps showing meanwhile (§15l).
 * A photo or a nutrition label is asked for by attaching the picture itself.
 */

const FIELDS: ChangeField[] = ['name', 'department_category', 'size', 'description_short', 'description_long', 'ingredients', 'nutrition_label', 'photo', 'other'];
const PICTURE_FIELDS: ChangeField[] = ['photo', 'nutrition_label'];
const LONG_FIELDS: ChangeField[] = ['description_short', 'description_long', 'ingredients', 'other'];
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

function uploadKind(field: ChangeField): AssetKind {
  if (field === 'photo') return 'product-photo';
  if (field === 'nutrition_label') return 'label-photo';
  return 'request-attachment';
}

export default function RequestChangeDialog({
  vendorId,
  detail,
  initialField,
  onClose,
  onSent,
}: {
  vendorId: string;
  detail: ProductDetail;
  initialField: ChangeField;
  onClose: () => void;
  onSent: (request: ChangeRequest) => void;
}) {
  const { t, i18n } = useTranslation();
  const openFor = (f: ChangeField) => detail.changeRequests.find((r) => r.state === 'open' && r.field === f) ?? null;

  const [field, setField] = useState<ChangeField>(initialField);
  const [proposed, setProposed] = useState(openFor(initialField)?.proposedValue ?? '');
  const [reason, setReason] = useState(openFor(initialField)?.reason ?? '');
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const isPicture = PICTURE_FIELDS.includes(field);
  const existing = openFor(field);
  const info = detail.info;
  const current: Partial<Record<ChangeField, string | null>> = {
    name: info.name,
    department_category: departmentPath(t, i18n.language, info.department, info.category),
    size: info.size,
    description_short: info.descriptionShort,
    description_long: info.descriptionLong,
    ingredients: info.ingredients,
    nutrition_label: info.nutritionOnFile ? t('products.detail.nutritionOnFile') : t('products.detail.notAdded'),
  };

  const pickField = (next: ChangeField) => {
    const open = openFor(next);
    setField(next);
    setProposed(open?.proposedValue ?? '');
    setReason(open?.reason ?? '');
    setAttachmentUrl(null);
    setError(null);
  };

  const upload = async (file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) return setError(t('products.errors.imageTooLarge'));
    setUploading(true);
    setError(null);
    try {
      const { cdnUrl } = await vendorApiClient.uploadAsset(vendorId, uploadKind(field), file);
      setAttachmentUrl(cdnUrl);
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setUploading(false);
    }
  };

  const send = async () => {
    setError(null);
    if (isPicture && !attachmentUrl) return setError(t('products.errors.attachment_required'));
    if (!isPicture && !proposed.trim()) return setError(t('products.errors.proposed_value_required'));
    setBusy(true);
    try {
      const request = await vendorApiClient.createChangeRequest(vendorId, detail.vendorProductId, {
        field,
        proposedValue: isPicture ? undefined : proposed.trim(),
        reason: reason.trim() || undefined,
        attachmentUrl: attachmentUrl ?? undefined,
      });
      onSent(request);
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth ariaLabelledBy="request-change-title" dataTestId="product-request-dialog">
      <DialogTitle id="request-change-title">{t('products.request.title')}</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: MUTED, fontSize: 14, mb: 2 }}>{info.name}</Typography>

        <Box data-testid="product-request-field">
          <Select
            ariaLabel={t('products.request.what')}
            label={t('products.request.what')}
            value={field}
            onChange={(_e, v) => pickField(v as ChangeField)}
            options={FIELDS.map((f) => ({ value: f, label: t(`products.request.field.${f}`) }))}
            size="small"
            fullWidth
          />
        </Box>

        {existing && (
          <Alert severity="info" sx={{ mt: 2 }} data-testid="product-request-replaces">
            {t('products.request.replaces')}
          </Alert>
        )}

        {field in current && (
          <Box sx={{ mt: 2 }}>
            <FieldLabel>{t('products.request.now')}</FieldLabel>
            <Typography
              sx={{ mt: 0.5, px: 1.5, py: 1.25, bgcolor: '#F1F2F4', border: `1px solid ${BORDER}`, borderRadius: '8px', fontSize: 14, color: HEADING, whiteSpace: 'pre-wrap' }}
              data-testid="product-request-current"
            >
              {current[field] || t('products.detail.notAdded')}
            </Typography>
          </Box>
        )}

        {!isPicture && (
          <Box sx={{ mt: 2 }}>
            <TextField
              ariaLabel={t(field === 'other' ? 'products.request.describe' : 'products.request.should')}
              label={t(field === 'other' ? 'products.request.describe' : 'products.request.should')}
              value={proposed}
              onChange={(e) => setProposed(e.target.value)}
              multiline={LONG_FIELDS.includes(field)}
              minRows={LONG_FIELDS.includes(field) ? 3 : undefined}
              size="small"
              fullWidth
              required
              slotProps={{ htmlInput: { maxLength: 5000 } }}
              dataTestId="product-request-proposed"
            />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <TextField
            ariaLabel={t('products.request.why')}
            label={t('products.request.why')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            fullWidth
            slotProps={{ htmlInput: { maxLength: 1000 } }}
            dataTestId="product-request-reason"
          />
        </Box>

        <Box
          sx={{ mt: 2, px: 2, py: 1.5, border: '1.5px dashed #C9CED6', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 2 }}
          data-testid="product-request-attachment"
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: HEADING }}>
              {t(isPicture ? `products.request.attach.${field}` : 'products.request.attach.optional')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              {attachmentUrl ? t('products.request.attach.added') : t('products.request.attach.hint')}
            </Typography>
          </Box>
          {attachmentUrl && (
            <Box component="img" src={attachmentUrl} alt={t('products.request.attach.previewAlt')} sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '8px' }} />
          )}
          {uploading ? (
            <Spinner color="brand" />
          ) : (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                data-testid="product-request-file"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void upload(f);
                  e.target.value = '';
                }}
              />
              <Button
                ariaLabel={t(attachmentUrl ? 'products.request.attach.replace' : 'products.request.attach.choose')}
                buttonText={t(attachmentUrl ? 'products.request.attach.replace' : 'products.request.attach.choose')}
                variant="outlinedGreen"
                disabled={busy}
                onClick={() => fileInput.current?.click()}
              />
            </>
          )}
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          {t('products.request.note')}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="product-request-error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button ariaLabel={t('common.cancel')} buttonText={t('common.cancel')} variant="text" onClick={onClose} disabled={busy} />
        <Button
          ariaLabel={t('products.request.send')}
          buttonText={t('products.request.send')}
          variant="contained"
          color="primary"
          onClick={() => void send()}
          disabled={busy || uploading}
          dataTestId="product-request-send"
        />
      </DialogActions>
    </Dialog>
  );
}
